import React from "react";
import PropTypes from "prop-types";
import { BackIcon } from "./Icons.js";
import {
  formatDateLabel,
  formatTimeLabel,
} from "../../utils/scheduleHelpers.js";

export const SCHEDULE_TABS = ["Past", "Upcoming"];

export const ScheduleTabs = ({
  activeTab,
  onTabChange,
  tabs = SCHEDULE_TABS,
}) => (
  <div
    className="schedule-tabs"
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "20px",
      padding: "4px 0",
      overflowX: "auto",
    }}
  >
    {tabs.map((tab) => (
      <button
        key={tab}
        type="button"
        className={`chip-btn ${activeTab === tab ? "active" : ""}`}
        onClick={() => onTabChange(tab)}
      >
        <span>{tab}</span>
      </button>
    ))}
  </div>
);

ScheduleTabs.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.string),
};

export const PastCondensedRow = ({
  game,
  awayTeamName,
  homeTeamName,
  awayScoreClass,
  homeScoreClass,
  userOutcome,
  canceled,
  onToggle,
}) => (
  <div className="past-condensed-container">
    <div className="past-condensed-datetime">
      {formatDateLabel(game.start_time)} {formatTimeLabel(game.start_time)}
      {canceled && (
        <span className="game-canceled-badge" style={{ marginLeft: "8px" }}>
          Canceled
        </span>
      )}
      {game.status === "rescheduled" && (
        <span
          className="game-rescheduled-badge"
          style={{
            marginLeft: "8px",
            color: "var(--color-turquoise)",
            fontWeight: 600,
            fontSize: "0.8rem",
          }}
        >
          Rescheduled
        </span>
      )}
    </div>
    <div className="past-condensed-main-row">
      <div className="past-condensed-left">
        <span
          className={`past-outcome-letter ${userOutcome?.statusClass || ""}`.trim()}
          data-testid={userOutcome ? "user-team-outcome" : undefined}
        >
          {userOutcome ? userOutcome.letter : ""}
        </span>
        <div className="past-condensed-teams">
          <div className="past-condensed-team-row">
            <span className="past-condensed-team-name">{awayTeamName}</span>
            <span className={`past-condensed-score ${awayScoreClass}`}>
              {game.away_score ?? "-"}
            </span>
          </div>
          <div className="past-condensed-team-row">
            <span className="past-condensed-team-name">{homeTeamName}</span>
            <span className={`past-condensed-score ${homeScoreClass}`}>
              {game.home_score ?? "-"}
            </span>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="game-chevron-btn"
        aria-label="Expand game details"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <BackIcon size="xs" className="game-chevron-icon condensed" />
      </button>
    </div>
  </div>
);

PastCondensedRow.propTypes = {
  game: PropTypes.object.isRequired,
  awayTeamName: PropTypes.string.isRequired,
  homeTeamName: PropTypes.string.isRequired,
  awayScoreClass: PropTypes.string,
  homeScoreClass: PropTypes.string,
  userOutcome: PropTypes.object,
  canceled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

export const ThreeColumnGameGrid = ({
  game,
  awayTeamName,
  homeTeamName,
  awayIsManaged,
  homeIsManaged,
  showScores,
  awayRecordFormatted,
  homeRecordFormatted,
  awayScoreClass,
  homeScoreClass,
  canceled,
  isExpanded,
  onToggle,
}) => (
  <div className="game-row-with-chevron">
    <div className="game-card-main-grid">
      <div className="game-team-col away">
        <span className="game-team-name-large">{awayTeamName}</span>
        {showScores ? (
          <span className={`game-team-score ${awayScoreClass}`}>
            {game.away_score ?? "-"}
          </span>
        ) : (
          <span className="game-team-sub">{awayRecordFormatted}</span>
        )}
      </div>

      <div className="game-middle-col">
        <span className="game-date-label">
          {formatDateLabel(game.start_time)}
        </span>
        <span className="game-time-label">
          {formatTimeLabel(game.start_time)}
        </span>
        {canceled && <span className="game-canceled-badge">Canceled</span>}
        {game.status === "rescheduled" && (
          <span
            className="game-rescheduled-badge"
            style={{
              color: "var(--color-turquoise)",
              fontWeight: 600,
              fontSize: "0.8rem",
            }}
          >
            Rescheduled
          </span>
        )}
        {game.status === "rescheduled" && game.original_start_time && (
          <span
            className="game-original-date"
            style={{
              textDecoration: "line-through",
              color: "var(--color-text-secondary)",
              fontSize: "0.75rem",
            }}
          >
            {formatDateLabel(game.original_start_time)}
          </span>
        )}
      </div>

      <div className="game-team-col home">
        <span className="game-team-name-large">{homeTeamName}</span>
        {showScores ? (
          <span className={`game-team-score ${homeScoreClass}`}>
            {game.home_score ?? "-"}
          </span>
        ) : (
          <span className="game-team-sub">{homeRecordFormatted}</span>
        )}
      </div>
    </div>

    <button
      type="button"
      className="game-chevron-btn"
      aria-label={isExpanded ? "Collapse game details" : "Expand game details"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      <BackIcon
        size="xs"
        className={`game-chevron-icon ${isExpanded ? "expanded" : "condensed"}`}
      />
    </button>
  </div>
);

ThreeColumnGameGrid.propTypes = {
  game: PropTypes.object.isRequired,
  awayTeamName: PropTypes.string.isRequired,
  homeTeamName: PropTypes.string.isRequired,
  awayIsManaged: PropTypes.bool,
  homeIsManaged: PropTypes.bool,
  showScores: PropTypes.bool,
  awayRecordFormatted: PropTypes.string,
  homeRecordFormatted: PropTypes.string,
  awayScoreClass: PropTypes.string,
  homeScoreClass: PropTypes.string,
  canceled: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};
